import { DataProvider } from '@refinedev/core';
import axios, { AxiosInstance } from 'axios';
import { ensureFreshAccessToken } from './tokenManager';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:33000/api/v1';

const axiosInstance: AxiosInstance = axios.create();

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await ensureFreshAccessToken(API_BASE_URL);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // CMS always edits/views canonical English fields. Without this, browser
    // Accept-Language (e.g. zh-HK) makes catalog APIs applyLocale() and swap name.
    config.headers['Accept-Language'] = 'en';
    return config;
  },
  (error) => Promise.reject(error),
);

// ============ Resource Route Map ============

interface ResourceRoute {
  /** Base path for list/getOne/delete operations */
  basePath: string;
  /** Separate path for list (defaults to basePath) */
  listPath?: string;
  /** Separate path for create (defaults to basePath) */
  createPath?: string;
  /** Separate path for update (defaults to basePath) */
  updatePath?: string;
  /** HTTP method for updates — defaults to 'put' */
  updateMethod?: 'patch' | 'put';
}

const RESOURCE_ROUTES: Record<string, ResourceRoute> = {
  'surveys':              { basePath: 'survey-admin/surveys', updateMethod: 'put' },
  'survey-screens':       { basePath: 'survey-admin/screens', updateMethod: 'put' },
  'survey-questions':     { basePath: 'survey-admin/questions', updateMethod: 'put' },
  'survey-responses':     { basePath: 'survey-admin/responses' },
  'food-logs':            { basePath: 'food-admin/logs' },
  'media-assets':         { basePath: 'media-admin/assets' },
  'visual-entities':      { basePath: 'vision-admin/entities' },
  'vision-queue-jobs':    { basePath: 'vision-admin/queue/jobs', listPath: 'vision-admin/queue/jobs' },
  'knowledge-bases':      { basePath: 'vision/knowledge-bases' },
  'content':              { basePath: 'content/admin', listPath: 'content/admin/list', createPath: 'content', updatePath: 'content' },
  'roles':                { basePath: 'rbac/roles' },
  'permissions':          { basePath: 'rbac/permissions' },
  'users':                { basePath: 'users' },
  'coin-transactions':    { basePath: 'admin/gamification/coin-transactions' },
  'gamification-tasks':   { basePath: 'admin/gamification/tasks', updateMethod: 'patch' },
  'pet-skins':            { basePath: 'admin/gamification/pet-skins', updateMethod: 'patch' },
  'gamification-audit':   { basePath: 'admin/gamification/audit' },
  'xp-transactions':      { basePath: 'admin/gamification/xp-transactions' },
  'badges':               { basePath: 'admin/challenge/badges', updateMethod: 'patch' },
  'levels':               { basePath: 'admin/levels', updateMethod: 'patch' },
  'languages':            { basePath: 'admin/languages', updateMethod: 'patch' },
  'notification-reminders': { basePath: 'admin/notification-reminders', updateMethod: 'patch' },
  'system-messages':      { basePath: 'admin/system-messages', updateMethod: 'patch' },
  'app-strings':          { basePath: 'admin/app-strings', updateMethod: 'patch' },
  'lang-pack-releases':   { basePath: 'admin/lang-packs' },
  'exercises':            { basePath: 'exercise-admin/exercises', updateMethod: 'patch' },
  'workout-plans':        { basePath: 'workout-admin/plans', updateMethod: 'patch' },
  'workout-logs':         { basePath: 'workout-admin/logs' },
  'subscription-plans':   { basePath: 'subscription-admin/plans', updateMethod: 'patch' },
  'subscriptions':        { basePath: 'subscription-admin/subscriptions' },
  'referrals':            { basePath: 'admin/referrals' },
  'referral-codes':       { basePath: 'admin/referral-codes', updateMethod: 'patch' },
  'sleep-steps':          { basePath: 'admin/sleep-steps' },
  'fasting-logs':         { basePath: 'fasting-admin/fasting-logs' },
  'mood-logs':            { basePath: 'mood-admin/mood-logs' },
  'health-sync-records':  { basePath: 'health-sync-admin/records', updateMethod: 'patch' },
  'equipment-catalog':    { basePath: 'equipment-catalog', updateMethod: 'patch' },
  'gym-branches':         { basePath: 'gym-branches', updateMethod: 'patch' },
  'ingredient-catalog':   { basePath: 'ingredient-catalog', updateMethod: 'patch' },
  'restaurants':          { basePath: 'restaurant', createPath: 'restaurant-admin/restaurants', updatePath: 'restaurant-admin/restaurants', updateMethod: 'patch' },
  'meals':                { basePath: 'meal', createPath: 'restaurant-admin/meals', updatePath: 'restaurant-admin/meals', updateMethod: 'patch' },
  'llm-providers':        { basePath: 'admin/llm/providers', updateMethod: 'patch' },
  'llm-prompts':          { basePath: 'admin/llm/prompts', updateMethod: 'patch' },
  'llm-call-logs':        { basePath: 'admin/llm/call-logs' },
  'otp-delivery-logs':    { basePath: 'admin/otp-delivery-logs' },
  'llm-models':           { basePath: 'admin/llm/models', updateMethod: 'patch' },
  'llm-playground-runs':  { basePath: 'admin/llm/playground-runs' },
  'tagging-logs':         { basePath: 'dish-catalog/tagging-logs' },
};

const resolveUrl = (resource: string, type: 'list' | 'one' | 'create' | 'update' | 'delete', id?: string): string => {
  const route = RESOURCE_ROUTES[resource];
  const base = route?.basePath ?? resource;

  let path: string;
  switch (type) {
    case 'list':
      path = route?.listPath ?? base;
      break;
    case 'create':
      path = route?.createPath ?? base;
      break;
    case 'update':
      path = `${route?.updatePath ?? base}/${id}`;
      break;
    case 'one':
    case 'delete':
      path = `${base}/${id}`;
      break;
  }

  return `${API_BASE_URL}/${path}`;
};

// ============ Response Normalizers ============

const normalizeListResponse = (response: any) => {
  if (Array.isArray(response.data)) {
    return { data: response.data, total: response.data.length };
  }
  if (response.data && Array.isArray(response.data.items)) {
    return { data: response.data.items, total: response.data.total ?? response.data.items.length };
  }
  if (response.data && Array.isArray(response.data.data)) {
    return { data: response.data.data, total: response.data.total ?? response.data.data.length };
  }
  return { data: [], total: 0 };
};

const normalizeSingleResponse = (response: any) => {
  return { data: response.data };
};

// ============ Data Provider ============

export { axiosInstance };

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }) => {
    const query: Record<string, any> = {};

    if (pagination) {
      query.page = pagination.current ?? 1;
      query.limit = pagination.pageSize ?? 20;
    }

    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.field && filter.value !== undefined) {
          query[filter.field] = filter.value;
        }
      });
    }

    if (sorters && sorters.length > 0) {
      query.sortBy = sorters[0].field ?? 'id';
      query.sortOrder = sorters[0].order;
    }

    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
        });
        return;
      }
      params.append(key, String(value));
    });
    const qs = params.toString();
    const url = `${resolveUrl(resource ?? '', 'list')}${qs ? `?${qs}` : ''}`;

    const response = await axiosInstance.get(url);
    return normalizeListResponse(response);
  },

  getOne: async ({ resource, id }) => {
    const url = resolveUrl(resource ?? '', 'one', String(id));
    const response = await axiosInstance.get(url);
    return normalizeSingleResponse(response);
  },

  create: async ({ resource, variables }) => {
    const url = resolveUrl(resource ?? '', 'create');
    const response = await axiosInstance.post(url, variables);
    return normalizeSingleResponse(response);
  },

  update: async ({ resource, id, variables }) => {
    const url = resolveUrl(resource ?? '', 'update', String(id));
    const route = RESOURCE_ROUTES[resource ?? ''];
    const method = route?.updateMethod ?? 'put';
    const response = await axiosInstance[method](url, variables);
    return normalizeSingleResponse(response);
  },

  deleteOne: async ({ resource, id }) => {
    const url = resolveUrl(resource ?? '', 'delete', String(id));
    const response = await axiosInstance.delete(url);
    return normalizeSingleResponse(response);
  },

  getMany: async ({ resource, ids }) => {
    const data = await Promise.all(
      ids.map(async (id) => {
        const url = resolveUrl(resource ?? '', 'one', String(id));
        const response = await axiosInstance.get(url);
        return response.data;
      }),
    );
    return { data };
  },

  getApiUrl: () => API_BASE_URL,

  custom: async ({ url, method, payload, query, headers }) => {
    let requestUrl = `${API_BASE_URL}${url}`;
    if (query) {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
      requestUrl = `${requestUrl}?${params.toString()}`;
    }

    let axiosResponse;
    switch (method) {
      case 'put':
      case 'post':
      case 'patch':
        axiosResponse = await axiosInstance[method](requestUrl, payload, { headers });
        break;
      case 'delete':
        axiosResponse = await axiosInstance.delete(requestUrl, { headers, data: payload });
        break;
      default:
        axiosResponse = await axiosInstance.get(requestUrl, { headers });
        break;
    }

    return { data: axiosResponse.data };
  },
};
